import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import Downshift from 'downshift';
import parse from 'html-react-parser';
import type { KeyboardEvent } from 'react';
import { useCallback, useRef } from 'react';

import { queries } from '~modules/media-library/controller/api/Queries';

type Props = {
  value: string;
  placeholder?: string;
  onChange: (value: string) => void;
  onEnter?: () => void;
  onSelect?: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
};

export function InputAutoComplete({ value, placeholder, onChange, onEnter, onSelect, onFocus, onBlur }: Props) {
  const refInput = useRef<HTMLInputElement>(null);

  const toggleFocus = useCallback(
    (focus: boolean) => {
      if (focus) {
        onFocus?.();
      }
      if (!focus) {
        onBlur?.();
      }
    },
    [onBlur, onFocus],
  );

  const { data: suggestions } = useQuery({
    ...queries.contribute.autoComplete(value),
    enabled: !!value && value.length >= 2,
    retry: false,
    refetchOnWindowFocus: false,
    initialData: [],
  });

  const handleFocusCatchClick = useCallback(() => {
    if (!refInput.current) return;
    if (refInput.current === document.activeElement) return;
    refInput.current.focus();
  }, []);

  const inputValueRegExp = new RegExp(value, 'i');

  return (
    <div>
      <Downshift inputValue={value} onInputValueChange={onChange} onChange={onSelect}>
        {({ getInputProps, getItemProps, highlightedIndex, isOpen, getMenuProps, closeMenu }) => (
          <div className="ml--search-input-container relative">
            <div
              className={clsx(
                'ml--input-search',
                'border-silver flex border-solid border p-2 rounded bg-white items-center overflow-hidden cursor-text justify-between',
              )}
              onClick={handleFocusCatchClick}
            >
              <input
                {...getInputProps({
                  placeholder,
                  value,
                  ref: refInput,
                  onFocus: () => toggleFocus(true),
                  onBlur: () => toggleFocus(false),
                  onKeyPress: (evt: KeyboardEvent<HTMLInputElement>) => {
                    if (evt.key === 'Enter') {
                      evt.preventDefault();
                      onEnter?.();
                      closeMenu();
                    }
                  },
                  className:
                    'ml--input-search--input appearance-none border-none outline-none leading-none text-black flex-grow',
                })}
              />
            </div>
            <div className="absolute top-full w-full z-50">
              {isOpen && Boolean(suggestions) && suggestions.length > 0 ? (
                <div
                  {...getMenuProps({
                    className:
                      'border-solid border border-silver p-1 rounded bg-white items-center overflow-hidden cursor-default justify-between shadow-lg',
                  })}
                >
                  {suggestions.slice(0, 10).map((item, index) => (
                    <div
                      key={item}
                      {...getItemProps({
                        index,
                        item,
                        className: clsx('py-1 px-2 rounded font-sans text-sm text-black', {
                          'bg-gray': index === highlightedIndex,
                        }),
                      })}
                    >
                      <div>{parse(item.replace(inputValueRegExp, '<strong>$&</strong>'))}</div>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        )}
      </Downshift>
    </div>
  );
}
